import React, { useState, useEffect } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const InputSales = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalSales, setTotalSales] = useState(0);
  const [salesPerFarmer, setSalesPerFarmer] = useState([]);
  const [salesPerCrop, setSalesPerCrop] = useState([]);
  const [farmerCropSales, setFarmerCropSales] = useState([]);

  const [viewType, setViewType] = useState("farmer");
  const [selectedFarmer, setSelectedFarmer] = useState("all");
  const [selectedCrop, setSelectedCrop] = useState("all");

  const [chartType, setChartType] = useState("pie");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const url = `/api/totalInputSalesProcurement/?userId=${localStorage.getItem(
      "id"
    )}&orgId=${localStorage.getItem("orgId")}`;

    fetchData({ url }, (res) => {
      setTotalSales(res.totalInputSales || 0);
      setSalesPerFarmer(res.salesPerFarmer || []);
      setSalesPerCrop(res.salesPerCrop || []);
      setFarmerCropSales(res.farmerCropSales || []);
      setLoading(false);
    });
  }, [fetchData]);

  let chartDataset = [];

  if (viewType === "farmer") {
    chartDataset =
      selectedFarmer === "all"
        ? salesPerFarmer
        : salesPerFarmer.filter(
            (f) => String(f.farmerId) === String(selectedFarmer)
          );
  } else {
    chartDataset =
      selectedCrop === "all"
        ? salesPerCrop
        : salesPerCrop.filter((c) => c.cropName === selectedCrop);
  }

  let displayAmount = totalSales;

  if (viewType === "farmer" && selectedFarmer !== "all") {
    const row = salesPerFarmer.find(
      (f) => String(f.farmerId) === String(selectedFarmer)
    );
    displayAmount = row ? row.totalAmount : 0;
  }

  if (viewType === "crop" && selectedCrop !== "all") {
    const cropRows = salesPerCrop.filter((c) => c.cropName === selectedCrop);
    displayAmount = cropRows.reduce(
      (sum, row) => sum + (row.totalAmount || 0),
      0
    );
  }

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: "20px",
          background: "#f5f9ee",
          borderRadius: "12px",
        }}
      >
        <div className="flex justify-between mb-4">
          <Select
            value={viewType}
            style={{ width: 180 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedCrop("all");
              setSelectedFarmer("all");
            }}
          >
            <Option value="farmer">Farmer Wise</Option>
            <Option value="crop">Crop Wise</Option>
          </Select>

          {viewType === "farmer" && (
            <Select
              value={selectedFarmer}
              style={{ width: 220 }}
              onChange={(v) => setSelectedFarmer(v)}
            >
              <Option value="all">All Farmers</Option>
              {salesPerFarmer.map((f) => (
                <Option key={f.farmerId} value={f.farmerId}>
                  {f.farmerName}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "crop" && (
            <Select
              value={selectedCrop}
              style={{ width: 220 }}
              onChange={(v) => setSelectedCrop(v)}
            >
              <Option value="all">All Crops</Option>
              {salesPerCrop.map((c, i) => (
                <Option key={i} value={c.cropName}>
                  {c.cropName}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160 }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        <div
          style={{
            textAlign: "center",
            marginBottom: "15px",
            padding: "10px",
            background: "#d2ebefff",
            borderRadius: "12px",
          }}
        >
          <h1 style={{ fontSize: "38px", fontWeight: "700", color: "#0a5139" }}>
            ₹ {displayAmount.toLocaleString()}
          </h1>

          <p style={{ fontSize: "18px", color: "#0a5139" }}>
            {viewType === "farmer"
              ? selectedFarmer === "all"
                ? "Total Input Sales"
                : "Sales of Selected Farmer"
              : selectedCrop === "all"
                ? "Total Input Sales"
                : "Sales of Selected Crop"}
          </p>
        </div>

        {chartType === "pie" && (
          <DynamicPieChart
            data={chartDataset}
            angleField="totalAmount"
            colorField={viewType === "farmer" ? "farmerName" : "cropName"}
            isMaximized={true}
          />
        )}

        {chartType === "column" && (
          <DynamicColumnChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalAmount"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}

        {chartType === "bar" && (
          <DynamicBarChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalAmount"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}

        {chartType === "line" && (
          <DynamicLineChart
            data={chartDataset}
            xField={viewType === "farmer" ? "farmerName" : "cropName"}
            yField="totalAmount"
            seriesField={viewType === "farmer" ? "farmerName" : "cropName"}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: "200px",
        width: "100%",
        padding: "18px",
        position: "relative",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{ fontSize: "36px", fontWeight: "700", color: "#3d5c26" }}
            >
              ₹ {totalSales.toLocaleString()}
            </h1>
            <p style={{ fontSize: "17px", marginTop: "-6px", color: "#3d5c26" }}>
              Input Sales
            </p>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "18px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#3d5c26",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default InputSales;
